import { FunctionComponent } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { useLocation } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isAIEnabled, $isLogined, $isSubAccount } from "../../store/user_info.ts";
import { gotoLoginPage } from "../../router_utils.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { AIActivityChart } from "../../components/ai_chart/ai_activity_chart.tsx";
import { requestGet, requestPost } from "../../utils/request.ts";
import { useToast } from "../../components/toast/toast.tsx";

interface AIModule {
    slug: string;
    name: string;
    description: string;
}

interface CameraAIConfig {
    camera_id: number;
    camera_name: string;
    is_enabled: boolean;
}

interface AIConfigResponse {
    ai_enabled?: boolean;
    modules: AIModule[];
    selected_module_slug: string | null;
    cameras: CameraAIConfig[];
    show_overview_ai_trend?: boolean;
    preferred_trend_days?: number;
}

export const AICenterPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const isAIEnabled = useStore($isAIEnabled);
    const [location, _setLocation] = useLocation();
    
    const [configData, setConfigData] = useState<AIConfigResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [updatingModule, setUpdatingModule] = useState(false);
    const [selectedChartCameraId, setSelectedChartCameraId] = useState<number | undefined>(undefined);
    const [trendDays, setTrendDays] = useState(7);

    useEffect(() => {
        document.title = t({ zh: "AI 分析中心", en: "AI Center" });
    }, [t]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? "/ai_center");
        } else {
            loadConfig();
        }
    }, [isLogined, location]);

    const loadConfig = async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await requestGet<AIConfigResponse>("/../ai/config");
            setConfigData(resp);
            if (typeof resp.preferred_trend_days === "number") {
                setTrendDays(resp.preferred_trend_days);
            }
        } catch (e) {
            console.error(e);
            const msg = (e as Error)?.message ?? "Failed to load AI config";
            setError(msg);
            toast(msg, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectModule = async (slug: string | null) => {
        if (!isAIEnabled) {
            toast(t({ zh: "AI 分析未开通，请联系管理员", en: "AI is not enabled. Please contact administrator." }), "error");
            return;
        }
        if (!configData) return;
        setUpdatingModule(true);
        try {
            await requestPost("/../ai/config", {
                action: 'select_module',
                module_slug: slug
            });
            // Update local state
            setConfigData({ 
                ...configData, 
                selected_module_slug: slug,
                // If disabled, also visually disable all cameras in local state (backend does not strictly require this but UI should reflect it)
                cameras: slug ? configData.cameras : configData.cameras.map(c => ({ ...c, is_enabled: false }))
            });
            toast(t({ zh: "AI 模型已更新", en: "AI Model updated" }), "success");
            // Refresh to update camera statuses if needed (optional)
            await loadConfig();
        } catch (e) {
            console.error(e);
            toast("Failed to select module", "error");
        } finally {
            setUpdatingModule(false);
        }
    };

    const handleToggleCamera = async (cameraId: number, enabled: boolean) => {
        // Optimistic update
        setConfigData(prev => {
            if (!prev) return null;
            const newCameras = prev.cameras.map(c => {
                if (c.camera_id === cameraId) {
                    return { ...c, is_enabled: enabled };
                }
                return c;
            });
            return { ...prev, cameras: newCameras };
        });

        try {
            await requestPost("/../ai/config", {
                action: 'toggle_camera',
                camera_id: cameraId,
                enabled: enabled
            });
            toast(t({ zh: "相机设置已更新", en: "Camera updated" }), "success");
        } catch (e) {
            console.error(e);
            toast((e as Error)?.message ?? "Failed to update camera", "error");
            loadConfig(); // Revert on error
        }
    };

    const handleToggleOverviewAITrend = async (enabled: boolean) => {
        if (!configData) return;
        setConfigData({ ...configData, show_overview_ai_trend: enabled });
        try {
            await requestPost("/../ai/config", {
                action: "toggle_overview_ai_trend",
                enabled,
            });
            toast(t({ zh: "设置已更新", en: "Setting updated" }), "success");
        } catch (e) {
            console.error(e);
            toast((e as Error)?.message ?? "Failed to update setting", "error");
            loadConfig();
        }
    };

    const handleSetPreferredTrendDays = async (days: number) => {
        setTrendDays(days);
        try {
            await requestPost("/../ai/config", {
                action: "set_preferred_trend_days",
                days,
            });
        } catch (e) {
            console.error(e);
            toast((e as Error)?.message ?? "Failed to update setting", "error");
            loadConfig();
        }
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (!isLogined) return <article aria-busy="true" class="container"></article>;

    const currentChartCameraName = selectedChartCameraId 
        ? configData?.cameras.find(c => c.camera_id === selectedChartCameraId)?.camera_name 
        : undefined;

    return (
        <div class="farpov_top_container" style={{ paddingBottom: "2rem" }}>
            <PageHeader 
                title={t({ zh: "AI 分析中心", en: "AI Analysis Center" })}
                subtitle={t({ zh: "配置并监控您的智能分析服务", en: "Configure and monitor your AI services" })}
            />

            {!isAIEnabled && (
                <article style={{ background: "rgba(255, 87, 34, 0.10)", padding: "1rem", marginTop: "1rem" }}>
                    <strong>{t({ zh: "AI 未开通", en: "AI Not Enabled" })}</strong>
                    <div style={{ marginTop: "0.25rem", opacity: 0.85 }}>
                        {t({ zh: "该账号尚未开通 AI 分析功能，请联系管理员在后台为您开启。", en: "AI analysis is not enabled for this account. Please contact the administrator to enable it." })}
                    </div>
                </article>
            )}

            {isSubAccount && (
                <article style={{ background: "rgba(255, 193, 7, 0.12)", padding: "1rem", marginTop: "1rem" }}>
                    <strong>{t({ zh: "子账户权限提示", en: "Sub-account Notice" })}</strong>
                    <div style={{ marginTop: "0.25rem", opacity: 0.85 }}>
                        {t({ zh: "子账户 AI 权限为只读，如需修改模型或开关请使用主账户操作。", en: "AI is read-only for sub-accounts. Please use the main account to change model or toggles." })}
                    </div>
                </article>
            )}
            
            {/* 1. Global Stats */}
            <div class="farpov_section_header_v3" style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 class="farpov_section_title_v3" style={{ marginBottom: 0 }}>
                    <Icon name="activity" size={18} />
                    {currentChartCameraName 
                        ? t({ zh: `${currentChartCameraName} 趋势`, en: `${currentChartCameraName} Trend` })
                        : t({ zh: "活跃度概览", en: "Activity Overview" })
                    }
                </h4>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <select
                        value={trendDays}
                        disabled={isSubAccount || loading || !isAIEnabled}
                        onChange={(e: Event) => handleSetPreferredTrendDays(Number((e.target as HTMLSelectElement).value))}
                        style={{ width: "auto", marginBottom: 0, height: "32px", padding: "0.2rem 0.5rem" }}
                    >
                        <option value={7}>{t({ zh: "近7天", en: "7 Days" })}</option>
                        <option value={30}>{t({ zh: "近30天", en: "30 Days" })}</option>
                        <option value={90}>{t({ zh: "近90天", en: "90 Days" })}</option>
                        <option value={180}>{t({ zh: "近180天", en: "180 Days" })}</option>
                        <option value={365}>{t({ zh: "近1年", en: "1 Year" })}</option>
                    </select>
                    {selectedChartCameraId && (
                        <button 
                            class="outline secondary" 
                            style={{ padding: "0.2rem 0.5rem", fontSize: "0.8rem", height: "32px", marginBottom: 0 }}
                            onClick={() => setSelectedChartCameraId(undefined)}
                        >
                            {t({ zh: "显示全部", en: "Show All" })}
                        </button>
                    )}
                </div>
            </div>
            {isAIEnabled ? <AIActivityChart cameraId={selectedChartCameraId} days={trendDays} /> : null}

            <div style={{ marginTop: "0.5rem", display: "flex", justifyContent: "center" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", margin: 0 }}>
                    <input
                        type="checkbox"
                        checked={!!configData?.show_overview_ai_trend}
                        disabled={isSubAccount || loading || !isAIEnabled}
                        onChange={(e) => handleToggleOverviewAITrend((e.target as HTMLInputElement).checked)}
                    />
                    <span style={{ opacity: isSubAccount ? 0.7 : 1 }}>
                        {t({ zh: "在相机总览显示 AI 活跃度趋势", en: "Show AI trend on Dashboard" })}
                    </span>
                </label>
            </div>

            {/* 2. Global Model Selection */}
            <div class="farpov_section_header_v3" style={{ marginTop: "2rem" }}>
                <h4 class="farpov_section_title_v3">
                    <Icon name="cpu" size={18} />
                    {t({ zh: "AI 模型选择", en: "AI Model Selection" })}
                </h4>
            </div>
            
            <article style={{ background: "var(--pico-card-background-color)", padding: "1.5rem" }}>
                <p style={{ marginBottom: "1rem", opacity: 0.8 }}>
                    {t({ zh: "请选择适用于您所有相机的 AI 分析模型：", en: "Select the AI model to apply to all your cameras:" })}
                </p>
                <div class="grid">
                    <div 
                        onClick={() => {
                            if (updatingModule || isSubAccount || !isAIEnabled) return;
                            handleSelectModule(null);
                        }}
                        style={{ 
                            padding: "1rem", 
                            border: configData?.selected_module_slug === null ? "2px solid var(--pico-muted-border-color)" : "1px solid var(--pico-card-border-color)",
                            borderRadius: "8px",
                            cursor: (isSubAccount || !isAIEnabled) ? "not-allowed" : (updatingModule ? "wait" : "pointer"),
                            opacity: (updatingModule || isSubAccount || !isAIEnabled) ? 0.6 : 1,
                            background: configData?.selected_module_slug === null ? "rgba(150, 150, 150, 0.1)" : "transparent",
                            position: "relative"
                        }}
                    >
                        {(!isAIEnabled) && (
                            <div style={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}>
                                <Icon name="lock" size={16} />
                            </div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                            <input 
                                type="radio" 
                                name="ai_module" 
                                checked={configData?.selected_module_slug === null} 
                                readOnly
                                style={{ margin: 0, marginRight: "0.5rem" }}
                            />
                            <strong>{t({ zh: "不使用 AI (已禁用)", en: "No AI (Disabled)" })}</strong>
                        </div>
                        <small style={{ display: "block", color: "var(--pico-muted-color)" }}>
                            {t({ zh: "关闭所有 AI 分析功能", en: "Disable all AI analysis features" })}
                        </small>
                    </div>

                    {configData?.modules.map(mod => (
                        <div 
                            key={mod.slug} 
                            onClick={() => {
                                if (updatingModule || isSubAccount || !isAIEnabled) return;
                                handleSelectModule(mod.slug);
                            }}
                            style={{ 
                                padding: "1rem", 
                                border: configData.selected_module_slug === mod.slug ? "2px solid var(--pico-primary)" : "1px solid var(--pico-card-border-color)",
                                borderRadius: "8px",
                                cursor: (isSubAccount || !isAIEnabled) ? "not-allowed" : (updatingModule ? "wait" : "pointer"),
                                opacity: (updatingModule || isSubAccount || !isAIEnabled) ? 0.6 : 1,
                                background: configData.selected_module_slug === mod.slug ? "rgba(var(--pico-primary-rgb), 0.05)" : "transparent",
                                position: "relative"
                            }}
                        >
                            {(!isAIEnabled) && (
                                <div style={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}>
                                    <Icon name="lock" size={16} />
                                </div>
                            )}
                            <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                                <input 
                                    type="radio" 
                                    name="ai_module" 
                                    checked={configData.selected_module_slug === mod.slug} 
                                    readOnly
                                    style={{ margin: 0, marginRight: "0.5rem" }}
                                />
                                <strong>{mod.name}</strong>
                            </div>
                            <small style={{ display: "block", color: "var(--pico-muted-color)" }}>{mod.description}</small>
                        </div>
                    ))}
                </div>
            </article>

            {/* 3. Camera Toggles */}
            <div class="farpov_section_header_v3" style={{ marginTop: "2rem" }}>
                <h4 class="farpov_section_title_v3">
                    <Icon name="camera" size={18} />
                    {t({ zh: "相机开关", en: "Camera Toggles" })}
                </h4>
            </div>

            {loading && !configData ? (
                <div aria-busy="true"></div>
            ) : error ? (
                 <div style={{ textAlign: "center", padding: "2rem", color: "var(--pico-del-color)" }}>
                    <Icon name="alert-triangle" size={32} />
                    <p style={{ marginTop: "1rem" }}>{t({ zh: "加载失败: " + error, en: "Failed to load: " + error })}</p>
                    <button onClick={loadConfig} class="outline">{t({ zh: "重试", en: "Retry" })}</button>
                 </div>
            ) : (
                <div class="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem" }}>
                    {configData?.cameras.map(cam => (
                        <article key={cam.camera_id} style={{ margin: 0, padding: "1rem", background: "var(--pico-card-background-color)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <Icon name="camera" size={20} />
                                <strong>{cam.camera_name}</strong>
                                <button 
                                    class={`outline ${selectedChartCameraId === cam.camera_id ? "primary" : "secondary"}`}
                                    style={{ padding: "0.2rem 0.5rem", fontSize: "0.7rem", marginLeft: "0.5rem", border: "none" }}
                                    onClick={() => {
                                        setSelectedChartCameraId(cam.camera_id);
                                        scrollToTop();
                                    }}
                                    title={t({ zh: "查看趋势", en: "View Trend" })}
                                >
                                    <Icon name="trending-up" size={14} />
                                </button>
                            </div>
                            <label class="switch">
                                <input 
                                    type="checkbox" 
                                    role="switch"
                                    checked={cam.is_enabled}
                                    disabled={!configData.selected_module_slug || isSubAccount || !isAIEnabled}
                                    onChange={(e) => {
                                        if (isSubAccount || !isAIEnabled) return;
                                        handleToggleCamera(cam.camera_id, (e.target as HTMLInputElement).checked);
                                    }}
                                />
                            </label>
                        </article>
                    ))}
                    {configData?.cameras.length === 0 && (
                        <p>{t({ zh: "未找到相机", en: "No cameras found" })}</p>
                    )}
                </div>
            )}
        </div>
    );
};
